import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import mutations from '@mailobj-browser/front/js/tree/mutations.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import resize from '@mailobj-browser/front/js/events/types/resize.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import wait from '@mailobj-browser/front/js/utils/wait.js'
import * as utilities from '#utilities/js/styles.js'
import { elements } from '../styles.js'
import inserted from '@mailobj-browser/front/js/tree/inserted.js'
import rendered from '@mailobj-browser/front/js/tree/rendered.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import { scope } from '#components/generics/js/selectors/scope.js'

const hideables = new WeakMap()
const containers = new WeakMap()
const mutationsObservers = new WeakMap()
const hideablesClone = new WeakMap()
const options = {
  childList: true,
  subtree: true
}
const { parseInt } = Number
export const getHideables = container => hideables.get(container)
export const events = object(null, {
  ready: 'childhideable-ready',
  manageParent: 'childhideable-parent',
})

export const manageChildren = container => {
  requestAnimationFrame(async () => {
    const { ready, manageParent } = events
    const { hideableScopeJsons } = hideables.get(container)
    const { ownerDocument } = container
    const { defaultView } = ownerDocument
    const { CustomEvent } = defaultView
    let { scrollWidth, clientWidth } = container

    if (scrollWidth > clientWidth) {
      for (const [element] of hideableScopeJsons) {
        if (element) {
          if (!element.classList.contains(utilities.modifiers.display.none)) {
            element.classList.add(utilities.modifiers.display.none)

            container.dispatchEvent(new CustomEvent(manageParent, { detail: element }))

            clientWidth = container.clientWidth
            scrollWidth = container.scrollWidth

            if (scrollWidth <= clientWidth) {
              break
            }
          }
        }
      }
    } else {
      const hideableScopeJsonsSorted = new Map([...hideableScopeJsons].sort(([, valueA], [, valueB]) => {
        const { child_hide_order: childHideOrderA } = valueA
        const { child_hide_order: childHideOrderB } = valueB

        return parseInt(childHideOrderB, 10) - parseInt(childHideOrderA, 10)
      }))

      for (const [element] of hideableScopeJsonsSorted) {
        if (element) {
          container.dispatchEvent(new CustomEvent(manageParent, { detail: element }))

          if (element.classList.contains(utilities.modifiers.display.none)) {
            element.classList.remove(utilities.modifiers.display.none)
            container.dispatchEvent(new CustomEvent(manageParent, { detail: element }))
          }

          clientWidth = container.clientWidth
          scrollWidth = container.scrollWidth

          if (scrollWidth > clientWidth) {
            element.classList.add(`${utilities.modifiers.display.none}`)
            container.dispatchEvent(new CustomEvent(manageParent, { detail: element }))

            break
          }
        }
      }
    }

    container.dispatchEvent(new CustomEvent(ready))
  })
}

const onResize = object(listener, {
  type: resize,
  once,
  passive,
  async task (defaultView) {
    await wait(150)

    const { document } = defaultView
    const nestedContainers = containers.get(document)

    for (const container of nestedContainers) {
      init(container)
    }

    this.listen(defaultView)
  }
})

const init = container => {
  const { ownerDocument } = container
  const { defaultView } = ownerDocument
  const children = all(`[data-scope-json]:not([data-contract~='childHideable.dropdownOpener'])`, container)
  const modal = closest(`.${elements.modal}`, container)
  let hideableScopeJsons = new Map()

  queueMicrotask(async () => {
    if (!containers.has(ownerDocument)) {
      containers.set(ownerDocument, new Set())
    }

    if (hideables.has(container)) {
      hideables.delete(container)
    }

    containers.get(ownerDocument).add(container)

    let childHideOrder, childHideableName
    for (const child of children) {
      if (hideablesClone.has(container)) {
        const { hideableScopeJsons: hideableScopeJsonsClone } = hideablesClone.get(container)
        if (!hideableScopeJsonsClone.has(child)) {
          return
        }

        const { child_hide_order, child_hideable_name } = hideableScopeJsonsClone.get(child)

        childHideOrder = child_hide_order
        childHideableName = child_hideable_name
      } else {
        const { child_hide_order, child_hideable_name } = scope(child)

        childHideOrder = child_hide_order
        childHideableName = child_hideable_name
      }

      hideableScopeJsons.set(child, {
        child_hide_order: childHideOrder,
        child_hideable_name: childHideableName
      })
    }

    hideableScopeJsons = new Map([...hideableScopeJsons].sort(([, valueA], [, valueB]) => {
      const { child_hide_order: childHideOrderA } = valueA
      const { child_hide_order: childHideOrderB } = valueB

      return parseInt(childHideOrderA, 10) - parseInt(childHideOrderB, 10)
    }))

    hideables.set(container, object(null, { hideableScopeJsons }))

    if (modal && !ownerDocument.contains(modal)) {
      await inserted(modal, one(`.${utilities.elements.aside_lightboxes}`, ownerDocument))
    }

    const { size } = hideableScopeJsons
    if (size && await rendered(children[0], container)) {
      manageChildren(container)
    }

    onResize.listen(defaultView)

    if (!mutationsObservers.has(container)) {
      mutationsObservers.set(container, new Set())
      observeButtonAdded(container)
    }
  })

  queueMicrotask(async () => {
    const nestedContainers = containers.get(ownerDocument)

    await removed(container)
    mutationsObservers.delete(container)
    hideables.delete(container)
    nestedContainers.delete(container)
  })
}

const observeButtonAdded = container => {
  queueMicrotask(async () => {
    for await (const { addedNodes } of mutations(container, options, removed(container))) {
      for (const addedNode of addedNodes) {
        const addedNodeScopeJson = scope(addedNode)
        const { child_hideable_name } = addedNodeScopeJson

        if (child_hideable_name) {
          if (!hideablesClone.has(container)) {
            hideablesClone.set(container, object(null, {
              hideableScopeJsons: hideables.get(container).hideableScopeJsons
            }))
          }

          for (const [key, value] of hideablesClone.get(container).hideableScopeJsons) {
            const { child_hideable_name: prevChildHideableName } = value

            if (prevChildHideableName === child_hideable_name) {
              const previousAction = key

              hideablesClone.get(container).hideableScopeJsons.set(
                addedNode,
                hideablesClone.get(container).hideableScopeJsons.get(previousAction)
              )

              hideablesClone.get(container).hideableScopeJsons.delete(previousAction)
              init(container)
              break
            }
          }
        }
      }
    }
  })
}

export default container => {
  init(container)
}
